import * as THREE from "three";
import {
  BloomEffect,
  ChromaticAberrationEffect,
  EffectComposer,
  EffectPass,
  HueSaturationEffect,
  NoiseEffect,
  RenderPass,
  SMAAEffect,
  ToneMappingEffect,
  ToneMappingMode,
  VignetteEffect,
} from "postprocessing";
import { QUALITY, type QualityPreset } from "../config";

export type RenderStack = {
  renderer: THREE.WebGLRenderer;
  composer: EffectComposer;
  bloom: BloomEffect;
  setQuality: (q: QualityPreset) => void;
  resize: (w: number, h: number) => void;
  render: (dt: number) => void;
  dispose: () => void;
};

export function createRenderer(canvas: HTMLCanvasElement, scene: THREE.Scene, camera: THREE.PerspectiveCamera): RenderStack {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: false,
    powerPreference: "high-performance",
    stencil: false,
    depth: true,
    alpha: false,
  });
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.NoToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.setClearColor(0x05070c, 1);
  renderer.autoClear = true;

  const composer = new EffectComposer(renderer, {
    frameBufferType: THREE.HalfFloatType,
  });
  composer.addPass(new RenderPass(scene, camera));

  const bloom = new BloomEffect({
    intensity: 0.38,
    luminanceThreshold: 0.28,
    luminanceSmoothing: 0.32,
    mipmapBlur: true,
    radius: 0.55,
  });
  const vignette = new VignetteEffect({
    eskil: false,
    offset: 0.38,
    darkness: 0.34,
  });
  const grain = new NoiseEffect({ premultiply: true });
  grain.blendMode.opacity.value = 0.045;
  const ca = new ChromaticAberrationEffect({
    offset: new THREE.Vector2(0.00055, 0.00035),
    radialModulation: true,
    modulationOffset: 0.15,
  });
  const grade = new HueSaturationEffect({ saturation: -0.06 });
  const tone = new ToneMappingEffect({ mode: ToneMappingMode.ACES_FILMIC });
  const smaa = new SMAAEffect();

  // Convolution effects (CA, SMAA) cannot share an EffectPass with other effects.
  composer.addPass(new EffectPass(camera, bloom, grade, vignette, grain, tone));
  composer.addPass(new EffectPass(camera, ca));
  composer.addPass(new EffectPass(camera, smaa));

  let quality: QualityPreset = "high";

  const apply = (q: QualityPreset) => {
    quality = q;
    const p = QUALITY[q];
    const pr = Math.min(window.devicePixelRatio, p.pixelRatio);
    renderer.setPixelRatio(pr);
    renderer.shadowMap.enabled = p.shadows;
    bloom.intensity = p.bloom ? 0.62 : 0;
    grain.blendMode.opacity.value = p.grain ? 0.045 : 0;
    ca.offset.set(p.ca ? 0.00055 : 0, p.ca ? 0.00035 : 0);
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    composer.setSize(window.innerWidth, window.innerHeight);
  };

  apply("high");

  return {
    renderer,
    composer,
    bloom,
    setQuality: apply,
    resize(w, h) {
      renderer.setSize(w, h, false);
      composer.setSize(w, h);
      camera.aspect = w / Math.max(1, h);
      camera.updateProjectionMatrix();
    },
    render(_dt) {
      composer.render();
    },
    dispose() {
      composer.dispose();
      renderer.dispose();
    },
  };
}

export function createNightEnv(renderer: THREE.WebGLRenderer) {
  const envScene = new THREE.Scene();
  const geo = new THREE.SphereGeometry(40, 32, 16);
  const mat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    uniforms: {},
    vertexShader: `varying vec3 vP; void main(){ vP = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }`,
    fragmentShader: `
      varying vec3 vP;
      void main(){
        float h = normalize(vP).y;
        vec3 zen = vec3(0.03, 0.05, 0.09);
        vec3 hor = vec3(0.09, 0.07, 0.05);
        vec3 grd = vec3(0.02, 0.02, 0.02);
        vec3 col = mix(hor, zen, smoothstep(0.0, 0.55, h));
        col = mix(grd, col, smoothstep(-0.25, 0.05, h));
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  });
  envScene.add(new THREE.Mesh(geo, mat));

  const card = (color: number, pos: THREE.Vector3, s: number, intensity = 1) => {
    const m = new THREE.Mesh(
      new THREE.PlaneGeometry(s, s),
      new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide })
    );
    m.position.copy(pos);
    m.lookAt(0, 0, 0);
    (m.material as THREE.MeshBasicMaterial).color.multiplyScalar(intensity);
    envScene.add(m);
  };
  card(0xffb347, new THREE.Vector3(12, 6, -8), 6, 2.2);
  card(0xffb347, new THREE.Vector3(-10, 5, 14), 5, 1.6);
  card(0x7ec8e3, new THREE.Vector3(-14, 8, -6), 4.5, 1.8);
  card(0x7ec8e3, new THREE.Vector3(8, 10, 16), 3.5, 1.4);
  card(0xff2a2a, new THREE.Vector3(4, 3, -16), 2.2, 1.3);
  card(0xe8e2d6, new THREE.Vector3(0, 18, 0), 7, 0.35);
  card(0x3a2a18, new THREE.Vector3(0, -8, 0), 30, 0.4);

  const pmrem = new THREE.PMREMGenerator(renderer);
  const env = pmrem.fromScene(envScene, 0.04).texture;
  pmrem.dispose();
  return env;
}
